import { parseResponse } from '$lib/client/responseHelpers';
import { isFilledObject } from '$lib/components/utils/checker';
import { onNextIdle } from '$lib/services/gtm/functions';

import type { DataLayer } from '@api/types';
import { getGtm } from '@api/user/stats';
import { getXhData } from '@api/utils';
import { localStorageService } from '@services/localStorage';
import { Cookie } from '@shared/cookies';
import { removeNullAndUndefined } from '@shared/utils/removeUndefined';
import { formatXhExperiments } from '@shared/utils/xhData';
import { getCookie, setCookie } from '@utils/cookies';

import { browser } from '$app/environment';

let gtmPromise: Promise<unknown> | null = null;
let initials: DataLayer | null = null;

const addData = async (data: object, checkInit = true, dataLayer = window.dataLayer): Promise<void> => {
    if (!isFilledObject(data)) {
        console.error('data isn\'t valid');
        return;
    }

    if (!gtmPromise && checkInit) {
        console.error('GTM isn\'t initialized');
        return;
    }
    try {
        if (checkInit) {
            await gtmPromise;
        }
        const cookieChannel = getCookie(Cookie.AttributionChannel);
        const channel = initials?.attribution_channel;
        if (channel && channel !== cookieChannel) {
            setCookie(Cookie.AttributionChannel, channel);
        }
        const xhData = getXhData();
        const xhExperiments = formatXhExperiments(
            xhData?.experiments ?? ''
        )?.map(ex => `${ex.type}_${ex.v}_${ex.g?.toUpperCase() ?? ''}`);

        const searchParams = new URLSearchParams(window.location.search);

        const dataLayerProperties = removeNullAndUndefined({
            attribution_channel: channel,
            xh_experiments: xhExperiments,
            xhStatsUid: xhData?.stats_uid,
            xhUserId: xhData?.user_id,
            orientation: searchParams.get('orientation'),
            platform: searchParams.get('platform'),
        });

        const isAudioOn = localStorageService.getItem(
            'microphone'
        );
        const isVideoOn = localStorageService.getItem(
            'camera'
        );

        try {
            const result = dataLayer.push({
                microphone_status: isAudioOn ?? false,
                camera_status: isVideoOn ?? false,
                ...data,
                ...dataLayerProperties,
            });

            if (result && typeof result.then === 'function') {
                await result.catch(err => {
                    console.error('Async push failed:', err);
                });
            }
        } catch (err) {
            console.error('Sync push failed:', err);
        }
    } catch (e) {
        console.error(e);
    }
};

const initGTM = (gtmId: string) => {
    return new Promise((resolve, reject) => {
        window.dataLayer = window.dataLayer || [];

        const script = document.createElement('script');
        script.async = true;
        script.defer = true;
        script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
        script.setAttribute('nonce', 'flirtify');

        script.onload = () => {
            const nativePush = window.dataLayer.push;
            window.dataLayer.push = async (...args) => {
                await onNextIdle();
                return nativePush(...args);
            };
            resolve(window.google_tag_manager?.[gtmId]);
        };

        script.onerror = (error) => {
            console.error('Failed to load GTM script', error);
            reject(error);
        };

        document.head.prepend(script);
    });
};

export const gtmService = () => {
    const init = async (gtmId: string, init?: { referrer: string; path: string; billing: string }) => {
        if (!browser) {
            console.error('GTM allowed only on the client side');
            return;
        }
        if (!gtmPromise) {
            const setInitial = init ? getGtm(init.referrer, init.path).then((response) => {
                const gtmInitials = parseResponse<DataLayer>(response) ?? null;
                if (gtmInitials) {
                    gtmInitials.billing_provider = init.billing;
                }
                initials = gtmInitials;
                return addData(initials, false);
            }) : Promise.resolve();
            gtmPromise = initGTM(gtmId).then(() => setInitial).then(() => {
                return addData({
                    event: 'gtm.js',
                    'gtm.start': new Date().getTime(),
                }, false);
            });
            await gtmPromise;
        }
    };

    return {
        init,
        addData,
    };
};
